import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { app } from '../firebase';
import './PayoutRequestForm.css';

const db = getFirestore(app);
const storage = getStorage(app);

const PayoutRequestForm = () => {
  const [formData, setFormData] = useState({
    accountHolderName: '',
    bankAccountNumber: '',
    bankName: '',
    ifscCode: '',
    upiId: '',
  });
  
  const [kycDocument, setKycDocument] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const auth = getAuth();
  const user = auth.currentUser;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        setMessage('Please upload only JPG, JPEG, PNG, or PDF files.');
        setMessageType('error');
        return;
      }

      if (file.size > maxSize) {
        setMessage('File size should be less than 5MB.');
        setMessageType('error');
        return;
      }

      setKycDocument(file);
      setMessage('');
    }
  };

  const uploadKycDocument = async (file, userId) => {
    try {
      const timestamp = Date.now();
      const fileName = `kyc_documents/${userId}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      console.log('Uploading KYC document:', fileName);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      console.log('KYC document uploaded successfully:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading KYC document:', error);
      throw new Error('Failed to upload KYC document');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setMessage('Please sign in to submit a payout request.');
      setMessageType('error');
      return;
    }

    if (!kycDocument) {
      setMessage('Please upload a KYC document.');
      setMessageType('error');
      return;
    }

    // Validate required fields
    const requiredFields = ['accountHolderName', 'bankAccountNumber', 'bankName', 'ifscCode'];
    const missingFields = requiredFields.filter(field => !formData[field].trim());
    
    if (missingFields.length > 0) {
      setMessage(`Please fill in all required fields: ${missingFields.join(', ')}`);
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      console.log('Starting payout request submission...');
      
      // Upload KYC document to Firebase Storage
      const kycDocumentUrl = await uploadKycDocument(kycDocument, user.uid);
      
      // Create payout request document in Firestore
      const payoutRequestData = {
        accountHolderName: formData.accountHolderName.trim(),
        bankAccountNumber: formData.bankAccountNumber.trim(),
        bankName: formData.bankName.trim(),
        ifscCode: formData.ifscCode.trim().toUpperCase(),
        upiId: formData.upiId.trim() || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        kycDocumentUrl: kycDocumentUrl,
        kycStatus: 'pending',
        kycUploadedAt: serverTimestamp(),
        kycVerifiedAt: null,
        kycVerifiedBy: null,
        payoutCompletedAt: null,
        status: 'pending',
        userId: user.uid,
      };

      const docRef = await addDoc(collection(db, 'payoutRequests'), payoutRequestData);
      console.log('Payout request created:', docRef.id);

      setMessage('Payout request submitted successfully! We will review your KYC documents and process your request within 3-5 business days.');
      setMessageType('success');
      
      // Reset form
      setFormData({
        accountHolderName: '',
        bankAccountNumber: '',
        bankName: '',
        ifscCode: '',
        upiId: '',
      });
      setKycDocument(null);
      
      // Reset file input
      const fileInput = document.getElementById('kycDocument');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Error submitting payout request:', error);
      setMessage('Failed to submit payout request. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="payout-form-container">
        <div className="auth-required">
          <h3>Authentication Required</h3>
          <p>Please sign in to submit a payout request.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payout-form-container">
      <div className="payout-form-card">
        <h2>Request Payout</h2>
        <p className="form-description">
          Submit your bank details and KYC documents to request a payout. 
          All requests are reviewed within 3-5 business days.
        </p>

        {message && (
          <div className={`message ${messageType}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="payout-form">
          <div className="form-section">
            <h3>Bank Account Details</h3>
            
            <div className="form-group">
              <label htmlFor="accountHolderName">Account Holder Name *</label>
              <input
                type="text"
                id="accountHolderName"
                name="accountHolderName"
                value={formData.accountHolderName}
                onChange={handleInputChange}
                required
                placeholder="Enter full name as per bank records"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bankAccountNumber">Bank Account Number *</label>
              <input
                type="text"
                id="bankAccountNumber"
                name="bankAccountNumber"
                value={formData.bankAccountNumber}
                onChange={handleInputChange}
                required
                placeholder="Enter your bank account number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bankName">Bank Name *</label>
              <input
                type="text"
                id="bankName"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                required
                placeholder="Enter your bank name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="ifscCode">IFSC Code *</label>
              <input
                type="text"
                id="ifscCode"
                name="ifscCode"
                value={formData.ifscCode}
                onChange={handleInputChange}
                required
                placeholder="Enter IFSC code"
                maxLength="11"
              />
            </div>

            <div className="form-group">
              <label htmlFor="upiId">UPI ID (Optional)</label>
              <input
                type="text"
                id="upiId"
                name="upiId"
                value={formData.upiId}
                onChange={handleInputChange}
                placeholder="Enter UPI ID (alternative to bank transfer)"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>KYC Document</h3>
            <p className="kyc-info">
              Upload a clear photo or scan of any government-issued ID (Aadhaar, PAN Card, Passport, Driving License)
            </p>
            
            <div className="form-group">
              <label htmlFor="kycDocument">KYC Document *</label>
              <input
                type="file"
                id="kycDocument"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf"
                required
              />
              <small className="file-info">
                Accepted formats: JPG, JPEG, PNG, PDF (Max size: 5MB)
              </small>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Payout Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PayoutRequestForm;